/**
 * The lookahead scheduler. A setInterval tick books notes LOOKAHEAD seconds
 * ahead of the audio clock, so an inference stall (or any main-thread jank)
 * never starves the note queue (architecture.md §1).
 *
 * SINCE AV-CORE PHASE 3 (flyway intent 2026-10-04-av-core-inline) the
 * scheduler itself is Chirp's single-source Clock, vendored at
 * ./vendor/av-core.mjs (Chirp 86227e8, stamp @c:a89de4971ab8). This file is the
 * O(1) DIALECT ADAPTER around it, and keeps gesture_synth's public API exactly:
 *
 *   · division. Here it is STEPS PER BEAT (4 = sixteenths at 4/4); the flock
 *     Clock takes BEATS PER STEP. Every call converts once: d_flock = 1 / d.
 *   · step. The flock reports a step's index as `beat`; its task handle also
 *     carries `.step` (an alias), which is what Task exposes.
 *   · epoch. nextGrid anchors at the context time when the transport was made,
 *     as before (passed to the Clock as `epoch`).
 *   · a throwing cb. The flock default silently cancels the task; here the
 *     error propagates out of the tick (the task stays and is retried on the
 *     next tick), exactly as the pre-av-core transport did.
 *
 * The catch-up guard, one-shots (`at`), nextGrid and stopAll are av-core's.
 * stepSeconds/stepsInWindow below are av-core's, behind the same 1/d adapter.
 * tests/vectors/av-core.json (generated from THIS module by
 * tools/gen-av-vectors.ts) is therefore an independent cross-check of
 * Chirp's code: `npm run vectors -- --check` must stay byte-clean.
 */

import {
  createClock,
  LOOKAHEAD as CORE_LOOKAHEAD,
  stepSeconds as coreStepSeconds,
  stepsInWindow as coreStepsInWindow,
} from './vendor/av-core.mjs';

export const LOOKAHEAD: number = CORE_LOOKAHEAD; // seconds — architecture.md invariant 3 wants >= 0.10

export interface TaskSpec {
  bpm: number;
  /** Steps per beat. 4 = sixteenths at 4/4. */
  division: number;
  /** Called once per step, with the exact audio time and the step index. */
  cb: (time: number, step: number) => void;
}

export interface Task {
  setBpm(bpm: number): void;
  setDivision(d: number): void;
  cancel(): void;
  readonly step: number;
}

export interface Transport {
  addTask(spec: TaskSpec): Task;
  /** Steps currently scheduled across all tasks — used by the debug surface. */
  readonly active: number;
  stopAll(): void;
  /**
   * The next grid line strictly ahead of the audio clock (plus a scheduling
   * margin), anchored at the transport's epoch. Pure arithmetic over
   * ctx.currentTime — works with or without a running task.
   */
  nextGrid(bpm: number, division: number): number;
  /**
   * One-shot: fire cb once `time` enters the lookahead window. The callback
   * books its own audio events, which means it samples whatever state exists
   * at FIRE-DECISION time — one lookahead before sound — rather than at the
   * moment the gesture armed it. That gap is what makes the vocabulary doc's
   * articulation option C (cutoff read when the grid fires, not when the
   * pinch closed) implementable at all.
   */
  at(time: number, cb: (time: number) => void): void;
}

export function createTransport(ctx: BaseAudioContext): Transport {
  const clock = createClock({
    context: ctx,
    epoch: ctx.currentTime,
    onError: (err) => { throw err; }, // keep gesture_synth's semantics: propagate, don't cancel
  });

  return {
    addTask({ bpm, division, cb }) {
      const h = clock.addTask({ bpm, division: 1 / division, cb });
      return {
        setBpm(v) { h.setBpm(v); },
        setDivision(v) { h.setDivision(1 / v); },
        cancel() { h.cancel(); },
        get step() { return h.step; },
      };
    },
    get active() { return clock.active; },
    stopAll() { clock.stopAll(); },
    // The context is always set here, so the flock's `null` (no context) cannot occur.
    nextGrid(bpm, division) { return clock.nextGrid(bpm, 1 / division) as number; },
    at(time, cb) { clock.at(time, cb); },
  };
}

/** Seconds per step (division = steps per beat). Pure — the tests pin this against the scheduler's use. */
export const stepSeconds = (bpm: number, division: number): number => coreStepSeconds(bpm, 1 / division);

/**
 * Steps a lookahead window would book, given the clock position and the next
 * pending step time. Pure, so the scheduler's core arithmetic is testable
 * without an AudioContext.
 */
export function stepsInWindow(now: number, nextTime: number, bpm: number, division: number, lookahead = LOOKAHEAD): number {
  return coreStepsInWindow(now, nextTime, bpm, 1 / division, lookahead);
}
